import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { Stack, Box, ClickAwayListener, Popper } from "@mui/material";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { MobileSelectDialog } from "./components/MobileSelectDialog.js";
import { SelectInput } from "./components/SelectInput/SelectInput.js";
import { SelectContext, SelectActionsContext } from "./context/SelectContext.js";
import "../../Button/Button.js";
import "../../InputControl/InputControl.js";
import "../../InputControl/Input/InputBase.js";
import "../../PhoneInputControl/PhoneInputControl.js";
import "../../PhoneInputControl/PhoneInput/PhoneInput.js";
import "../../IconButton/IconButton.js";
import "../../common/ControlBody.js";
import { useBreakpoint } from "../../../hooks/useBreakpoint.js";
import { createEvent } from "../../../hooks/useInternalControlled.js";
import { modifiers } from "./PopperModifiers.js";
import { SearchPanel } from "./SearchPanel.js";
import { inheritDataTestId } from "../../../utils/dataTestId.js";
import { CloseReasons } from "./types/enums.js";
import "./MultiSelect/context/MultiSelectContext.js";
import { Hr } from "./components/Hr.js";
import "../../../icons/24/System/Check.js";
import "../../../icons/24/Arrow/ChevronDown.js";
import "../../TextareaControl/TextareaControl.js";
import "../../SearchControl/SearchControl.js";
import "../../../icons/24/System/Search.js";
import "../../FormattedPhone/FormattedPhoneNumber.js";
import "../../Avatar/styles.js";
import "../../CheckboxGroup/Checkbox/styles.js";
import "../../CheckboxGroup/styles.js";
import "../../IconButtonNew/IconButton.js";
import "../../RadioGroup/Radio/styles.js";
import "../../RadioGroup/styles.js";
import "../../RatingButton/styles.js";
import "../../FloatingActionButton/styles.js";
import "../../SortingButton/styles.js";
import "../../SquareButton/styles.js";
import "../../Toggle/styles.js";
import "../../Dialog/Dialog.js";
import "../../Dialog/StyledDialog.js";
import "../../Chips/Chip/styles.js";
import "../../ResultMarksNumber/styles.js";
import "../../Progress/ProgressCircle/styles.js";
import "../../Loaders/LoaderCircle/styles.js";
import "../../Loaders/LoaderLine/styles.js";
import "../../SegmentedControl/styles.js";
import "../../ResultMarksText/styles.js";
import "../../Link/styles.js";
import "../../Tag/Tag.js";
import "../../DatePicker/constants.js";
import "../../DatePicker/components/RangePicker/context/DateRangePickerContext.js";
import "../../DatePicker/components/RangePicker/context/DateRangePickerContextActions.js";
import "../../DatePicker/shared/fragments/CalendarCell/CalendarCell.js";
import "../../../external/classnames/index.js";
import { Scrollbar } from "../../Scrollbar/Scrollbar.js";
import "../../DatePicker/shared/fragments/MonthYearSelector/YearSelector.js";
import "../../DatePicker/components/DatePicker/context/DatePickerContext.js";
import "../../DatePicker/components/DatePicker/context/DatePickerContextActions.js";
import "../../MobileBottomSheet/MobileBottomSheet.js";
import "../../List/ListItem/ListItem.js";
import "../../Nowrap.js";
import "../../List/ListItemTree/TreeListItem.js";
import "../../List/ListItemTree/components/Label.js";
const dummy = () => {
}, Select = ({
  disabled,
  children,
  value,
  onChange,
  onClose,
  onCloseMobile,
  name,
  placeholder,
  noMobileView,
  mobileHeader,
  onReachEnd,
  onReachEndThreshold,
  dropdownFooterRow,
  dropdownHeaderRow,
  onSearch,
  inputRef,
  renderValue,
  mobileBackButton,
  ...props
}) => {
  const [el, setElement] = useState(null), [filter, setFilter] = useState(""), handleContainerClick = (e) => {
    !el && !disabled && setElement(e.currentTarget);
  }, { xsOnly } = useBreakpoint("xs"), mobileViewEnabled = xsOnly && !noMobileView, open = !!el;
  useEffect(() => {
    setElement(null);
  }, [mobileViewEnabled]);
  const cbRefs = useRef({ onClose, onCloseMobile });
  cbRefs.current.onClose = onClose, cbRefs.current.onCloseMobile = onCloseMobile;
  const handleClose = useCallback((reason) => {
    var _a, _b;
    setElement(null), (_b = (_a = cbRefs.current).onClose) == null || _b.call(_a, reason);
  }, []), handleCloseMobile = useCallback(() => {
    var _a, _b;
    handleClose(CloseReasons.CANCEL), (_b = (_a = cbRefs.current).onCloseMobile) == null || _b.call(_a);
  }, [handleClose]), externalActionsRef = useRef({ onChange, handleClose });
  externalActionsRef.current.onChange = onChange, externalActionsRef.current.handleClose = handleClose;
  const context = useMemo(() => {
    const { value: primValue } = value ?? {};
    return {
      value: primValue ?? value,
      filter
    };
  }, [value, filter]), actions = useMemo(() => ({
    onChange: ({ value: value2, option }) => {
      var _a, _b;
      (_b = (_a = externalActionsRef.current).onChange) == null || _b.call(
        _a,
        createEvent({ value: value2, name }),
        option
      ), mobileViewEnabled || externalActionsRef.current.handleClose(CloseReasons.APPLY);
    },
    onClose: () => {
      externalActionsRef.current.handleClose(CloseReasons.CANCEL);
    }
  }), [mobileViewEnabled, name]), renderValueRef = useRef(renderValue);
  renderValueRef.current = renderValue;
  const inputValue = useMemo(() => {
    var _a;
    const { label } = value ?? {};
    return label ? [label] : [((_a = renderValueRef == null ? void 0 : renderValueRef.current) == null ? void 0 : _a.call(renderValueRef, value)) ?? value];
  }, [value]), select = /* @__PURE__ */ jsx(Stack, { className: "child-container", onClick: handleContainerClick, children: /* @__PURE__ */ jsx(
    SelectInput,
    {
      open,
      value: inputValue,
      placeholder,
      disabled,
      name,
      ref: inputRef,
      mobileView: mobileViewEnabled,
      ...props
    }
  ) }), listHeaderContent = onSearch ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      SearchPanel,
      {
        onSearch,
        setFilter,
        mobileView: mobileViewEnabled
      }
    ),
    dropdownHeaderRow
  ] }) : dropdownHeaderRow, listFooterContent = dropdownFooterRow;
  let renderContent = () => /* @__PURE__ */ jsx(Fragment, {});
  return mobileViewEnabled ? renderContent = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Box, { children: select }),
    /* @__PURE__ */ jsx(
      MobileSelectDialog,
      {
        open,
        header: mobileHeader,
        handleClose,
        handleCloseMobile,
        listHeaderContent,
        listFooterContent,
        onReachEndThreshold,
        onReachEnd,
        mobileBackButton,
        children
      }
    )
  ] }) : renderContent = () => /* @__PURE__ */ jsx(
    ClickAwayListener,
    {
      onClickAway: open ? () => handleClose(CloseReasons.CANCEL) : dummy,
      children: /* @__PURE__ */ jsxs(Box, { className: "click-away-container", children: [
        /* @__PURE__ */ jsx(
          Popper,
          {
            open,
            anchorEl: el,
            placement: "bottom-start",
            modifiers,
            children: /* @__PURE__ */ jsxs(Stack, { sx: styles.dropdownContainer, children: [
              listHeaderContent && /* @__PURE__ */ jsxs(Stack, { sx: styles.header, children: [
                listHeaderContent,
                /* @__PURE__ */ jsx(Hr, {})
              ] }),
              /* @__PURE__ */ jsx(
                Scrollbar,
                {
                  "data-test-id": inheritDataTestId(props, "scrollbar"),
                  onYReachThreshold: onReachEndThreshold,
                  onYReachEnd: onReachEnd,
                  children
                }
              ),
              listFooterContent && /* @__PURE__ */ jsxs(Stack, { sx: styles.footer, children: [
                /* @__PURE__ */ jsx(Hr, {}),
                listFooterContent
              ] })
            ] })
          }
        ),
        select
      ] })
    }
  ), /* @__PURE__ */ jsx(SelectContext.Provider, { value: context, children: /* @__PURE__ */ jsx(SelectActionsContext.Provider, { value: actions, children: renderContent() }) });
}, styles = {
  dropdownContainer: {
    boxShadow: (t) => t.boxShadows.dropdown,
    maxHeight: 300,
    backgroundColor: (t) => t.palette.common.white
  },
  footer: {},
  header: {},
  ddContent: {
    backgroundColor: (t) => t.palette.common.white
  }
};
export {
  Select
};
//# sourceMappingURL=Select.js.map
